import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, getAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { HR_ROLES, PAYROLL_ROLES, AUDIT_ROLES, ASSET_ROLES, FINANCE_ROLES, ASSET_DASHBOARD_ROLES } from "@hrms/types";
import { SpecularButton } from "../components/ui/specular-button.js";
import { SelectField } from "../components/ui/select-field.js";
import { usePageBack } from "../context/PageBackContext.js";

function decodeToken(token: string): { role: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? "") };
  } catch { return { role: "" }; }
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)",
};
const btnStyle: React.CSSProperties = {
  padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
  fontWeight: 600, cursor: "pointer", border: "none",
  background: "var(--color-brand-600)", color: "#fff",
};
const cardStyle: React.CSSProperties = {
  background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: "16px",
};

interface Summary {
  headcount: { active: number; onLeave: number };
  pendingApprovals: { leaves: number; expenses: number };
  openItTickets: number;
  onboardingInProgress: number;
  documentsExpiringWithin90Days: number;
}

interface AuditLogUser {
  id: string;
  email: string;
  employee: { firstName: string; lastName: string } | null;
}

interface AuditLogRow {
  id: string;
  userId: string;
  eventType: string;
  resourceType: string | null;
  resourceId: string | null;
  statusCode: number | null;
  ip: string;
  path: string | null;
  createdAt: string;
  user: AuditLogUser | null;
}

interface AuditMeta { total: number; page: number; limit: number; totalPages: number; }

interface TimeSpentUser {
  id: string; email: string;
  employee: { firstName: string; lastName: string; employeeCode: string } | null;
}
interface TimeSpentSummaryRow { userId: string; activeMs: number; requestCount: number; daysActive: number; user: TimeSpentUser | null; }
interface TimeSpentDailyRow { userId: string; date: string; activeMs: number; requestCount: number; }
interface TimeSpentHeatmapRow { userId: string; dayOfWeek: number; hourOfDay: number; activeMs: number; requestCount: number; }
interface TimeSpentResponse { from: string; to: string; summary: TimeSpentSummaryRow[]; daily: TimeSpentDailyRow[]; heatmap: TimeSpentHeatmapRow[]; }

type Granularity = "day" | "week" | "month";

// ── Asset persona dashboards (HRMS-0501) ─────────────────────────────────────

interface AssetOffice { id: string; name: string; city: string }

// Folded from the old AssetsPage "Dashboard" tab — same field names, unchanged endpoint.
interface DashboardSpend { currency: string; approvedTotal: number; receivedTotal: number }
interface DashboardOfficeRow { officeId: string; officeName: string; city: string; totalRequests: number; pendingApproval: number; spend: DashboardSpend[] }
interface ProcurementStockRequest {
  id: string; category: string; model: string | null; quantity: number; status: string; financeRemarks: string | null;
  office: { id: string; name: string; city: string };
  requestedBy: { firstName: string; lastName: string };
  financeActionBy: { firstName: string; lastName: string } | null;
  receivedBy: { firstName: string; lastName: string } | null;
  invoiceS3Key: string | null;
  aiVerificationResult: { verdict: string } | null;
}
interface ProcurementSummary {
  scope: { officeId: string | null };
  statusCounts: Record<string, number>;
  spendByCurrency: DashboardSpend[];
  aiVerification: { flagged: number; clean: number; pending: number };
  byOffice: DashboardOfficeRow[];
  recent: ProcurementStockRequest[];
}
interface StockSummaryRow { officeId: string | null; category: string; model: string | null; count: number }

interface ItDashboard {
  scope: { officeId: string | null; locked: boolean };
  tickets: {
    openTotal: number; breachedTotal: number; dueSoon: number; unassigned: number; resolved7d: number;
    backlogByPriority: { priority: string; open: number; breached: number; breachRate: number }[];
    byType: { ticketType: string; count: number }[];
  };
  verification: {
    activeCampaigns: number; awaitingResponse: number; openExceptions: number;
    campaigns: {
      id: string; title: string; office: { id: string; name: string; city: string } | null; startedAt: string;
      total: number; verified: number; pending: number; exceptions: number; completionPct: number;
    }[];
  };
  imports: {
    stagedBatches: number; errorRowsAwaitingFix: number; unmatchedRowsAwaitingFix: number; importedLast30d: number;
    recentBatches: {
      id: string; fileName: string; status: string; totalRows: number; validRows: number; errorRows: number;
      importedRows: number; unmatchedRows: number; uploadedAt: string; office: { id: string; name: string; city: string } | null;
    }[];
  };
  expiry: {
    warrantyExpired: number; warrantyExpiring30d: number; warrantyExpiring90d: number; rentalsEnding60d: number;
    licenseSeats: number; licenseCount: number; licensesNoRenewalOwner: number;
    soonestExpiries: {
      id: string; assetTag: string; name: string; category: string; warrantyExpiry: string; status: string;
      office: { name: string; city: string } | null;
    }[];
  };
  estate: { status: string; count: number }[];
}

interface FinanceDashboard {
  scope: { officeId: string | null; locked: boolean };
  disposals: {
    statusCounts: Record<string, number>;
    pendingByCurrency: { currency: string; count: number; value: number }[];
    approved90dByCurrency: { currency: string; count: number; value: number }[];
    autoApproved90d: number; financeApproved90d: number;
    pendingQueue: {
      id: string; estimatedValue: string; currency: string; submittedAt: string | null; daysWaiting: number;
      wipeRequired: boolean; wipeConfirmed: boolean;
      asset: { assetTag: string; name: string; category: string; office: { name: string; city: string } | null };
    }[];
  };
  custody: { awaitingConfirmation: number; heldInCustody: number };
  recurringCost: {
    rentalByCurrency: { currency: string; monthly: number; count: number }[];
    telecomByCurrency: { currency: string; monthly: number; count: number }[];
  };
  bookedCost: { byCurrency: { currency: string; value: number; count: number }[]; missingPrice: number };
}

interface ExecDashboard {
  scope: { officeId: string | null; locked: boolean };
  kpis: {
    totalActive: number; assigned: number; available: number; utilisation: number | null;
    bookedByCurrency: { currency: string; value: number }[]; missingPrice: number;
  };
  verificationCompliance: { rate: number | null; activeCampaigns: number };
  ownershipMix: { ownershipType: string; count: number }[];
  byOffice: {
    officeId: string | null; officeName: string; total: number; assigned: number; available: number; inRepair: number;
    bookedByCurrency: Record<string, number>;
  }[];
  byCategory: { category: string; count: number; bookedByCurrency: Record<string, number>; pctOfEstate: number }[];
  writeOffTrend: { month: string; byCurrency: Record<string, number> }[];
}

function userLabel(u: TimeSpentUser | null, fallbackId: string): string {
  if (!u) return fallbackId;
  return u.employee ? `${u.employee.firstName} ${u.employee.lastName}` : u.email;
}

function formatDuration(ms: number): string {
  if (ms <= 0) return "—";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

function heatColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return "transparent";
  const t = Math.min(1, value / max);
  return `rgba(34, 197, 94, ${0.15 + t * 0.75})`;
}

function StatTile({ label, value, sub, tone = "default" }: {
  label: string; value: string | number; sub?: string; tone?: "default" | "warn" | "error" | "success";
}) {
  const toneStyle: React.CSSProperties =
    tone === "warn"    ? { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" } :
    tone === "error"   ? { background: "var(--color-error-bg)",   color: "var(--color-error-fg)" } :
    tone === "success" ? { background: "var(--color-success-bg)", color: "var(--color-success-fg)" } : {};
  return (
    <div style={{ ...cardStyle, minWidth: 150, padding: "14px 16px", marginBottom: 0, ...toneStyle }}>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.8 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, marginTop: 2, opacity: 0.7 }}>{sub}</div>}
    </div>
  );
}
function money(currency: string, n: number): string {
  return `${currency} ${Math.round(n).toLocaleString("en-IN")}`;
}
function pctLabel(n: number | null): string {
  return n == null ? "—" : `${Math.round(n * 100)}%`;
}
function barCell(value: number, max: number): React.CSSProperties {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return { background: `linear-gradient(to right, var(--color-brand-100) ${pct}%, transparent ${pct}%)`, padding: "6px 10px" };
}

function startOfWeek(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = d.getUTCDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function bucketKeyFor(date: string, granularity: Granularity): string {
  if (granularity === "day") return date;
  if (granularity === "month") return date.slice(0, 7);
  return startOfWeek(date);
}

function bucketLabel(key: string, granularity: Granularity): string {
  // timeZone: "UTC" is required here -- key is an IST calendar-date string computed server-side
  // (see reports.route.ts's dateKeyInTz), and without forcing UTC, toLocaleDateString silently
  // reinterprets the embedded date through the *viewer's* local browser timezone, which can shift
  // the displayed label a day off from the actual bucket (e.g. showing "Jul 30" for a key that is
  // really "2026-07-31" if the browser's local zone is behind UTC).
  if (granularity === "month") {
    return new Date(`${key}-01T00:00:00Z`).toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
  }
  if (granularity === "week") {
    const start = new Date(`${key}T00:00:00Z`);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}–${end.toLocaleDateString("en-US", { day: "numeric", timeZone: "UTC" })}`;
  }
  return new Date(`${key}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function bucketDaily(daily: TimeSpentDailyRow[], granularity: Granularity) {
  const matrix = new Map<string, Map<string, { activeMs: number; requestCount: number }>>();
  const colSet = new Set<string>();
  for (const row of daily) {
    const key = bucketKeyFor(row.date, granularity);
    colSet.add(key);
    if (!matrix.has(row.userId)) matrix.set(row.userId, new Map());
    const userMap = matrix.get(row.userId)!;
    const cell = userMap.get(key) ?? { activeMs: 0, requestCount: 0 };
    cell.activeMs += row.activeMs; cell.requestCount += row.requestCount;
    userMap.set(key, cell);
  }
  const columns = [...colSet].sort().map(key => ({ key, label: bucketLabel(key, granularity) }));
  return { matrix, columns };
}

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dayOfWeekLabel(dateKey: string): string {
  return DOW_LABELS[new Date(`${dateKey}T00:00:00Z`).getUTCDay()]!;
}

function buildHeatmapGrid(heatmap: TimeSpentHeatmapRow[], userId: string) {
  const grid = new Map<string, { activeMs: number; requestCount: number }>();
  for (const row of heatmap) {
    if (userId !== "ALL" && row.userId !== userId) continue;
    const key = `${row.dayOfWeek}|${row.hourOfDay}`;
    const cell = grid.get(key) ?? { activeMs: 0, requestCount: 0 };
    cell.activeMs += row.activeMs; cell.requestCount += row.requestCount;
    grid.set(key, cell);
  }
  return grid;
}

function describePath(eventType: string, path: string | null): string {
  if (!path) return eventType;
  if (/\/api\/employees\/[^/]+\/documents/.test(path)) return "Accessed employee documents";
  if (path === "/api/me/profile" || path.startsWith("/api/me")) return "Viewed own profile";
  if (/\/api\/employees\/[^/]+$/.test(path) && eventType === "RECORD_UPDATED") return "Updated employee profile";
  if (/\/api\/employees\/[^/]+$/.test(path)) return "Viewed employee profile";
  if (path === "/api/employees" && eventType === "RECORD_CREATED") return "Created employee";
  if (path === "/api/employees") return "Viewed employee list";
  if (/\/api\/employees\/[^/]+\/onboarding/.test(path)) return "Accessed onboarding";
  if (path.startsWith("/api/leave")) return "Accessed leave requests";
  if (path.startsWith("/api/reports/summary")) return "Viewed reports dashboard";
  if (path.startsWith("/api/reports/audit")) return "Viewed audit logs";
  if (path.startsWith("/api/reports")) return "Accessed reports";
  if (path.startsWith("/api/admin/holidays")) return "Accessed holiday calendar";
  if (path.startsWith("/api/admin")) return "Performed admin action";
  if (path.startsWith("/api/lookup")) return "Loaded dropdown data";
  if (path.startsWith("/api/documents")) return "Accessed documents";
  if (path.startsWith("/api/payroll")) return "Accessed payroll";
  if (path.startsWith("/api/it-tickets")) return "Accessed IT tickets";
  if (path.startsWith("/api/auth")) return "Authenticated";
  return path;
}

const TABS = ["Summary", "Headcount", "Leave", "Documents", "Payroll", "Audit Logs", "User Activity", "Executive", "IT", "Finance"] as const;
type Tab = typeof TABS[number];

export default function ReportsPage() {
  usePageBack("/dashboard");
  const accessToken = getAccessToken() ?? "";
  const { role } = decodeToken(accessToken);
  const isHR      = HR_ROLES.has(role);
  const isPayroll = PAYROLL_ROLES.has(role);
  const isAudit   = AUDIT_ROLES.has(role);
  const isAssetIT      = ASSET_ROLES.has(role);
  // Parity with the old AssetsPage Dashboard tab this replaces, which was gated
  // `isIT || isFinance` — narrowing to FINANCE_ROLES alone would silently revoke
  // procurement analytics from IT.
  const isAssetFinance = FINANCE_ROLES.has(role) || ASSET_ROLES.has(role);
  const isAssetExec    = ASSET_DASHBOARD_ROLES.has(role) || isAssetIT || isAssetFinance;

  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const t = searchParams.get("tab");
    if ((TABS as readonly string[]).includes(t ?? "")) return t as Tab;
    if (isHR)            return "Summary";
    if (isPayroll)       return "Payroll";
    if (isAudit)         return "Audit Logs";
    if (isAssetIT)       return "IT";
    if (isAssetFinance)  return "Finance";
    return "Executive";
  });
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [days, setDays] = useState("90");
  const [auditUserSearch, setAuditUserSearch] = useState("");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditLogs, setAuditLogs] = useState<AuditLogRow[]>([]);
  const [auditMeta, setAuditMeta] = useState<AuditMeta>({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [auditPage, setAuditPage] = useState(1);
  const [collapsedUsers, setCollapsedUsers] = useState<Set<string>>(new Set());

  const [tsFrom, setTsFrom] = useState(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [tsTo, setTsTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [tsData, setTsData] = useState<TimeSpentResponse | null>(null);
  const [tsLoading, setTsLoading] = useState(false);
  const [tsGranularity, setTsGranularity] = useState<Granularity>("week");
  const [tsHeatmapUserId, setTsHeatmapUserId] = useState("ALL");
  const matrixScrollRef = useRef<HTMLDivElement>(null);

  const [assetOffices, setAssetOffices] = useState<AssetOffice[]>([]);
  const [dashOffice, setDashOffice]     = useState("");
  const [recentStatus, setRecentStatus] = useState("");
  const [dashLoading, setDashLoading]   = useState(false);
  const [execData, setExecData] = useState<ExecDashboard | null>(null);
  const [itData,   setItData]   = useState<ItDashboard | null>(null);
  const [finData,  setFinData]  = useState<FinanceDashboard | null>(null);
  const [finProc,  setFinProc]  = useState<ProcurementSummary | null>(null);
  const [finStock, setFinStock] = useState<StockSummaryRow[]>([]);

  // The matrix can have 30+ columns in Day view; without this, the most recent (usually most
  // relevant) dates sit scrolled out of view to the right, behind the sticky Total column, and
  // look like they're missing entirely rather than just unscrolled-to.
  useEffect(() => {
    const el = matrixScrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [tsData, tsGranularity]);

  useEffect(() => {
    if (tab === "Summary" && isHR) {
      setLoading(true);
      api.get<Summary>("/reports/summary")
        .then(r => setSummary(r.data))
        .catch((err) => { logError("ReportsPage.loadSummary", err); setError("Failed to load summary"); })
        .finally(() => setLoading(false));
    }
  }, [tab]);

  async function loadTimeSpent() {
    setTsLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ from: tsFrom, to: tsTo });
      const r = await api.get<TimeSpentResponse>(`/reports/user-time-spent?${params}`);
      setTsData(r.data);
    } catch (err) { logError("ReportsPage.loadTimeSpent", err); setError("Failed to load user activity"); }
    finally { setTsLoading(false); }
  }

  useEffect(() => {
    if (tab === "User Activity" && isAudit && !tsData) void loadTimeSpent();
  }, [tab]);

  async function loadExecDashboard() {
    setDashLoading(true); setError("");
    try {
      const r = await api.get<ExecDashboard>("/asset-dashboards/executive");
      setExecData(r.data);
    } catch (err) { logError("ReportsPage.loadExecDashboard", err); setError("Failed to load executive dashboard"); }
    finally { setDashLoading(false); }
  }
  async function loadItDashboard() {
    setDashLoading(true); setError("");
    try {
      const p = new URLSearchParams(); if (dashOffice) p.set("officeId", dashOffice);
      const r = await api.get<ItDashboard>(`/asset-dashboards/it?${p}`);
      setItData(r.data);
    } catch (err) { logError("ReportsPage.loadItDashboard", err); setError("Failed to load IT dashboard"); }
    finally { setDashLoading(false); }
  }
  async function loadFinanceDashboard() {
    setDashLoading(true); setError("");
    try {
      const p = new URLSearchParams(); if (dashOffice) p.set("officeId", dashOffice);
      const [fin, proc, stock] = await Promise.all([
        api.get<FinanceDashboard>(`/asset-dashboards/finance?${p}`),
        api.get<ProcurementSummary>(`/stock-requests/dashboard-summary?${p}`),
        api.get<StockSummaryRow[]>(`/assets/stock-summary?${p}`),
      ]);
      setFinData(fin.data); setFinProc(proc.data); setFinStock(stock.data);
    } catch (err) { logError("ReportsPage.loadFinanceDashboard", err); setError("Failed to load finance dashboard"); }
    finally { setDashLoading(false); }
  }

  useEffect(() => {
    if (tab === "Executive" && isAssetExec)    void loadExecDashboard();
    if (tab === "IT"        && isAssetIT)      void loadItDashboard();
    if (tab === "Finance"   && isAssetFinance) void loadFinanceDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dashOffice]);

  useEffect(() => {
    if (!isAssetIT && !isAssetFinance) return;
    api.get<AssetOffice[]>("/assets/offices").then(r => setAssetOffices(r.data))
       .catch(err => logError("ReportsPage.loadAssetOffices", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAuditLogs(p = 1) {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ limit: "50", page: String(p) });
      if (auditFrom) params.set("from", auditFrom);
      if (auditTo)   params.set("to",   auditTo);
      const r = await api.get<{ data: AuditLogRow[]; meta: AuditMeta }>(`/reports/audit-logs?${params}`);
      setAuditLogs(r.data.data);
      setAuditMeta(r.data.meta);
      setAuditPage(p);
    } catch (err) { logError("ReportsPage.loadAuditLogs", err); setError("Failed to load audit logs"); }
    finally { setLoading(false); }
  }

  function downloadCsv(path: string, filename: string) {
    const token = getAccessToken() ?? "";
    fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => { logError("ReportsPage.downloadCsv", err); setError("Download failed"); });
  }

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
    fontWeight: active ? 700 : 500, cursor: "pointer",
    border: "1px solid " + (active ? "var(--color-brand-600)" : "var(--color-surface-200)"),
    background: active ? "var(--color-brand-600)" : "var(--color-surface-0)",
    color: active ? "#fff" : "var(--color-surface-700)",
  });

  if (!isHR && !isPayroll && !isAudit && !isAssetIT && !isAssetFinance && !isAssetExec) {
    return <div style={{ padding: "32px" }}>Access denied.</div>;
  }

  return (
    <div style={{ padding: "32px", maxWidth: "1100px" }}>
      <h1 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "24px" }}>Reports & Analytics</h1>

      {error && (
        <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: "16px", fontSize: "13px" }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
        {TABS.filter(t => {
          if (t === "Payroll") return isPayroll;
          if (t === "Audit Logs") return isAudit;
          if (t === "User Activity") return isAudit;
          if (t === "Executive") return isAssetExec;
          if (t === "IT") return isAssetIT;
          if (t === "Finance") return isAssetFinance;
          return isHR;
        }).map(t => (
          <button key={t} style={tabBtnStyle(tab === t)} onClick={() => setTab(t as Tab)}>{t}</button>
        ))}
      </div>

      {tab === "Summary" && (
        <div>
          {loading && <p>Loading…</p>}
          {summary && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
              {[
                { label: "Active Employees",           value: summary.headcount.active },
                { label: "On Leave",                   value: summary.headcount.onLeave },
                { label: "Pending Leave Approvals",    value: summary.pendingApprovals.leaves },
                { label: "Pending Expense Approvals",  value: summary.pendingApprovals.expenses },
                { label: "Open IT Tickets",            value: summary.openItTickets },
                { label: "Onboarding In Progress",     value: summary.onboardingInProgress },
                { label: "Docs Expiring (90 days)",    value: summary.documentsExpiringWithin90Days },
              ].map(({ label, value }) => (
                <div key={label} style={{ ...cardStyle, textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--color-brand-600)" }}>{value}</div>
                  <div style={{ fontSize: "13px", color: "var(--color-surface-500)", marginTop: "4px" }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Headcount" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>Headcount Report</h2>
          <button style={btnStyle} onClick={() => downloadCsv("/reports/headcount?format=csv", "headcount.csv")}>
            Download CSV
          </button>
          <p style={{ fontSize: "13px", color: "var(--color-surface-500)", marginTop: "12px" }}>
            Exports all active + on-leave employees with department, office, and status breakdown.
          </p>
        </div>
      )}

      {tab === "Leave" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>Leave Utilisation</h2>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "16px" }}>
            <label style={{ fontSize: "13px" }}>Year:</label>
            <input style={{ ...inputStyle, width: "100px" }} type="number" value={year} onChange={e => setYear(e.target.value)} />
            <button style={btnStyle} onClick={() => downloadCsv(`/reports/leave-utilisation?year=${year}&format=csv`, `leave-${year}.csv`)}>
              Download CSV
            </button>
          </div>
          <p style={{ fontSize: "13px", color: "var(--color-surface-500)" }}>
            Shows allocated vs used leave by employee and type for the selected year.
          </p>
        </div>
      )}

      {tab === "Documents" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>Document Expiry Report</h2>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "16px" }}>
            <label style={{ fontSize: "13px" }}>Expiring within:</label>
            <SelectField specular className="w-auto" value={days} onChange={e => setDays(e.target.value)}>
              {["7", "30", "60", "90"].map(d => <option key={d} value={d}>{d} days</option>)}
            </SelectField>
            <button style={btnStyle} onClick={() => downloadCsv(`/reports/document-expiry?days=${days}&format=csv`, `doc-expiry-${days}d.csv`)}>
              Download CSV
            </button>
          </div>
          <p style={{ fontSize: "13px", color: "var(--color-surface-500)" }}>
            Documents expiring within the selected window, sorted by expiry date.
          </p>
        </div>
      )}

      {tab === "Payroll" && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>Payroll Discrepancy Report</h2>
          <button style={btnStyle} onClick={() => downloadCsv("/reports/payroll-discrepancies?format=csv", "payroll-discrepancies.csv")}>
            Download CSV
          </button>
          <p style={{ fontSize: "13px", color: "var(--color-surface-500)", marginTop: "12px" }}>
            All payroll runs flagged with discrepancies with affected line items.
          </p>
        </div>
      )}

      {tab === "Audit Logs" && (() => {
        const filteredLogs = auditUserSearch
          ? auditLogs.filter(l => {
              const u = l.user;
              const name = u?.employee ? `${u.employee.firstName} ${u.employee.lastName}`.toLowerCase() : "";
              const email = (u?.email ?? "").toLowerCase();
              const q = auditUserSearch.toLowerCase();
              return name.includes(q) || email.includes(q);
            })
          : auditLogs;

        const userGroups = new Map<string, { user: AuditLogUser | null; logs: AuditLogRow[]; latestAt: string }>();
        for (const log of filteredLogs) {
          const uid = log.userId;
          if (!userGroups.has(uid)) userGroups.set(uid, { user: log.user, logs: [], latestAt: log.createdAt });
          userGroups.get(uid)!.logs.push(log);
        }
        const sortedGroups = [...userGroups.entries()].sort(
          (a, b) => new Date(b[1].latestAt).getTime() - new Date(a[1].latestAt).getTime()
        );

        return (
          <div style={cardStyle}>
            <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>Audit Log Viewer</h2>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" }}>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "4px" }}>Search by name or email</div>
                <input
                  style={{ ...inputStyle, width: "220px" }} placeholder="e.g. Baldev or baldev@…"
                  value={auditUserSearch} onChange={e => setAuditUserSearch(e.target.value)}
                />
              </div>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "4px" }}>From</div>
                <input style={inputStyle} type="datetime-local" value={auditFrom} onChange={e => setAuditFrom(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "4px" }}>To</div>
                <input style={inputStyle} type="datetime-local" value={auditTo} onChange={e => setAuditTo(e.target.value)} />
              </div>
              <button style={btnStyle} onClick={() => loadAuditLogs(1)}>Search</button>
              <button
                style={{ ...btnStyle, background: "var(--color-surface-700)" }}
                onClick={() => {
                  const params = new URLSearchParams({ format: "csv" });
                  if (auditFrom) params.set("from", auditFrom);
                  if (auditTo)   params.set("to", auditTo);
                  downloadCsv(`/reports/audit-logs?${params}`, "audit-logs.csv");
                }}
              >
                Export CSV
              </button>
            </div>

            {loading && <p style={{ color: "var(--color-surface-500)", fontSize: "13px" }}>Loading…</p>}

            {!loading && auditLogs.length === 0 && (
              <p style={{ fontSize: "13px", color: "var(--color-surface-500)" }}>Use filters and click Search.</p>
            )}

            {sortedGroups.map(([uid, { user, logs, latestAt }]) => {
              const name = user?.employee
                ? `${user.employee.firstName} ${user.employee.lastName}`
                : (user?.email ?? uid);
              const initials = user?.employee
                ? `${user.employee.firstName[0] ?? ""}${user.employee.lastName[0] ?? ""}`.toUpperCase()
                : (user?.email?.[0]?.toUpperCase() ?? "?");
              const isCollapsed = collapsedUsers.has(uid);
              const toggle = () => setCollapsedUsers(s => {
                const n = new Set(s);
                if (n.has(uid)) n.delete(uid); else n.add(uid);
                return n;
              });

              return (
                <div key={uid} style={{ border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", marginBottom: "12px", overflow: "hidden" }}>
                  <div
                    onClick={toggle}
                    style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px", cursor: "pointer", background: "var(--color-surface-0)", borderBottom: isCollapsed ? "none" : "1px solid var(--color-surface-100)" }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--color-brand-100)", color: "var(--color-brand-700)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "13px", flexShrink: 0 }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "14px", color: "var(--color-surface-900)" }}>{name}</div>
                      <div style={{ fontSize: "12px", color: "var(--color-surface-500)" }}>{user?.email ?? uid}</div>
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--color-surface-500)", textAlign: "right" }}>
                      <div style={{ fontWeight: 600 }}>{logs.length} events</div>
                      <div>{new Date(latestAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <span style={{ fontSize: "11px", color: "var(--color-brand-600)", marginLeft: 8, userSelect: "none" }}>
                      {isCollapsed ? "▼" : "▲"}
                    </span>
                  </div>

                  {!isCollapsed && (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <tbody>
                        {logs.map(l => (
                          <tr key={l.id} style={{ borderBottom: "1px solid var(--color-surface-50)" }}>
                            <td style={{ padding: "6px 16px", color: "var(--color-surface-400)", whiteSpace: "nowrap", width: 80 }}>
                              {new Date(l.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </td>
                            <td style={{ padding: "6px 10px", color: "var(--color-surface-700)" }}>
                              {describePath(l.eventType, l.path)}
                            </td>
                            <td style={{ padding: "6px 10px", width: 50, textAlign: "center" }}>
                              <span style={{ fontSize: "11px", fontWeight: 700, color: (l.statusCode ?? 0) >= 400 ? "var(--color-error)" : "var(--color-success)" }}>
                                {l.statusCode ?? "—"}
                              </span>
                            </td>
                            <td style={{ padding: "6px 16px", fontFamily: "monospace", color: "var(--color-surface-400)", width: 120, textAlign: "right" }}>
                              {l.ip}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}

            {auditMeta.totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "16px", fontSize: "13px", color: "var(--color-surface-600)" }}>
                <span>Showing {(auditMeta.page - 1) * auditMeta.limit + 1}–{Math.min(auditMeta.page * auditMeta.limit, auditMeta.total)} of {auditMeta.total}</span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <SpecularButton disabled={auditPage <= 1} onClick={() => loadAuditLogs(auditPage - 1)}>Prev</SpecularButton>
                  <SpecularButton disabled={auditPage >= auditMeta.totalPages} onClick={() => loadAuditLogs(auditPage + 1)}>Next</SpecularButton>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {tab === "User Activity" && (() => {
        const summaryRows = tsData?.summary ?? [];
        const daily = tsData?.daily ?? [];
        const heatmap = tsData?.heatmap ?? [];
        const { matrix, columns } = bucketDaily(daily, tsGranularity);

        let matrixMax = 0;
        for (const userMap of matrix.values()) {
          for (const cell of userMap.values()) matrixMax = Math.max(matrixMax, cell.activeMs);
        }

        const colTotals = new Map<string, number>();
        for (const col of columns) {
          let total = 0;
          for (const userMap of matrix.values()) total += userMap.get(col.key)?.activeMs ?? 0;
          colTotals.set(col.key, total);
        }

        const heatGrid = buildHeatmapGrid(heatmap, tsHeatmapUserId);
        let heatMax = 0;
        for (const cell of heatGrid.values()) heatMax = Math.max(heatMax, cell.activeMs);

        return (
          <div style={cardStyle}>
            <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}>User Activity</h2>
            <p style={{ fontSize: "12px", color: "var(--color-surface-500)", marginBottom: "16px" }}>
              Active time is estimated from request activity (gaps under 10 minutes count toward active time) — not raw session length, so leaving a tab open doesn't inflate the numbers.
            </p>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" }}>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "4px" }}>From</div>
                <input style={inputStyle} type="date" value={tsFrom} onChange={e => setTsFrom(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "4px" }}>To</div>
                <input style={inputStyle} type="date" value={tsTo} onChange={e => setTsTo(e.target.value)} />
              </div>
              <button style={btnStyle} onClick={() => void loadTimeSpent()}>{tsLoading ? "Loading…" : "Load"}</button>
              <div style={{ display: "flex", gap: "4px", marginLeft: "auto" }}>
                {(["day", "week", "month"] as Granularity[]).map(g => (
                  <button
                    key={g}
                    onClick={() => setTsGranularity(g)}
                    style={{
                      padding: "6px 14px", borderRadius: "var(--radius-md)", fontSize: "12px", fontWeight: 600, cursor: "pointer",
                      border: "1px solid " + (tsGranularity === g ? "var(--color-brand-600)" : "var(--color-surface-200)"),
                      background: tsGranularity === g ? "var(--color-brand-600)" : "var(--color-surface-0)",
                      color: tsGranularity === g ? "#fff" : "var(--color-surface-700)",
                      textTransform: "capitalize",
                    }}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {tsLoading && <p>Loading…</p>}

            {!tsLoading && summaryRows.length === 0 && (
              <p style={{ fontSize: "13px", color: "var(--color-surface-500)" }}>No activity recorded in this range.</p>
            )}

            {!tsLoading && summaryRows.length > 0 && (
              <>
                <div ref={matrixScrollRef} style={{ overflowX: "auto", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", marginBottom: "28px" }}>
                  <table style={{ borderCollapse: "collapse", fontSize: "12px", width: "100%" }}>
                    <thead>
                      {tsGranularity === "day" && (
                        <tr style={{ background: "var(--color-surface-50)" }}>
                          <th style={{ position: "sticky", left: 0, background: "var(--color-surface-50)" }}></th>
                          {columns.map(col => (
                            <th key={col.key} style={{ padding: "2px 10px 0", textAlign: "center", fontWeight: 500, fontSize: "10px", color: "var(--color-surface-500)", whiteSpace: "nowrap", textTransform: "uppercase" }}>
                              {dayOfWeekLabel(col.key)}
                            </th>
                          ))}
                          <th style={{ position: "sticky", right: 0, background: "var(--color-surface-50)" }}></th>
                        </tr>
                      )}
                      <tr style={{ background: "var(--color-surface-50)" }}>
                        <th style={{ position: "sticky", left: 0, background: "var(--color-surface-50)", padding: "8px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)", whiteSpace: "nowrap", zIndex: 1 }}>
                          User
                        </th>
                        {columns.map(col => (
                          <th key={col.key} style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)", whiteSpace: "nowrap" }}>
                            {col.label}
                          </th>
                        ))}
                        <th style={{ position: "sticky", right: 0, background: "var(--color-surface-50)", padding: "8px 12px", textAlign: "right", fontWeight: 700, borderBottom: "1px solid var(--color-surface-200)", whiteSpace: "nowrap" }}>
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryRows.map(s => {
                        const userMap = matrix.get(s.userId);
                        return (
                          <tr key={s.userId} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                            <td style={{ position: "sticky", left: 0, background: "var(--color-surface-0)", padding: "8px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>
                              {userLabel(s.user, s.userId)}
                            </td>
                            {columns.map(col => {
                              const cell = userMap?.get(col.key);
                              const ms = cell?.activeMs ?? 0;
                              return (
                                <td
                                  key={col.key}
                                  title={cell ? `${formatDuration(ms)} · ${cell.requestCount} actions` : "No activity"}
                                  style={{ padding: "8px 10px", textAlign: "center", background: heatColor(ms, matrixMax), whiteSpace: "nowrap" }}
                                >
                                  {ms > 0 ? formatDuration(ms) : ""}
                                </td>
                              );
                            })}
                            <td style={{ position: "sticky", right: 0, background: "var(--color-surface-0)", padding: "8px 12px", textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>
                              {formatDuration(s.activeMs)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: "2px solid var(--color-surface-300)", background: "var(--color-surface-50)" }}>
                        <td style={{ position: "sticky", left: 0, background: "var(--color-surface-50)", padding: "8px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>
                          Org Total
                        </td>
                        {columns.map(col => (
                          <td key={col.key} style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {formatDuration(colTotals.get(col.key) ?? 0)}
                          </td>
                        ))}
                        <td style={{ position: "sticky", right: 0, background: "var(--color-surface-50)", padding: "8px 12px", textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>
                          {formatDuration(summaryRows.reduce((sum, s) => sum + s.activeMs, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                    <h3 style={{ fontSize: "14px", fontWeight: 600, margin: 0 }}>Activity by Hour of Day</h3>
                    <SelectField size="sm" className="w-auto" value={tsHeatmapUserId} onChange={e => setTsHeatmapUserId(e.target.value)}>
                      <option value="ALL">All Users</option>
                      {summaryRows.map(s => <option key={s.userId} value={s.userId}>{userLabel(s.user, s.userId)}</option>)}
                    </SelectField>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", fontSize: "11px" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "4px 6px" }}></th>
                          {Array.from({ length: 24 }, (_, h) => (
                            <th key={h} style={{ padding: "2px 4px", fontWeight: 500, color: "var(--color-surface-500)", textAlign: "center", width: 26 }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DOW_LABELS.map((label, dow) => (
                          <tr key={dow}>
                            <td style={{ padding: "2px 8px", fontWeight: 600, color: "var(--color-surface-600)", whiteSpace: "nowrap" }}>{label}</td>
                            {Array.from({ length: 24 }, (_, hour) => {
                              const cell = heatGrid.get(`${dow}|${hour}`);
                              const ms = cell?.activeMs ?? 0;
                              return (
                                <td
                                  key={hour}
                                  title={cell ? `${label} ${hour}:00 — ${formatDuration(ms)} · ${cell.requestCount} actions` : `${label} ${hour}:00 — no activity`}
                                  style={{ width: 26, height: 22, background: heatColor(ms, heatMax), border: "1px solid var(--color-surface-100)" }}
                                />
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {tab === "Executive" && isAssetExec && (
        <div>
          {dashLoading && <p style={{ fontSize: 14, color: "var(--color-surface-500)" }}>Loading…</p>}
          {execData && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                <StatTile label="Active Assets" value={execData.kpis.totalActive} />
                <StatTile label="Assigned" value={execData.kpis.assigned} />
                <StatTile label="Available" value={execData.kpis.available} />
                <StatTile label="Utilisation" value={pctLabel(execData.kpis.utilisation)} />
                {execData.kpis.bookedByCurrency.map(c => (
                  <StatTile key={c.currency} label={`Booked Value (${c.currency})`} value={money(c.currency, c.value)} />
                ))}
                {execData.kpis.missingPrice > 0 && (
                  <StatTile label="Missing Purchase Price" value={execData.kpis.missingPrice} tone="warn" />
                )}
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Verification Compliance</h3>
              <div style={{ marginBottom: 20 }}>
                {execData.verificationCompliance.activeCampaigns === 0
                  ? <p style={{ color: "var(--color-surface-500)", fontSize: 13 }}>No active campaign</p>
                  : <StatTile label={`Across ${execData.verificationCompliance.activeCampaigns} active campaign(s)`} value={pctLabel(execData.verificationCompliance.rate)} />}
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Ownership Mix</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                {execData.ownershipMix.map(o => (
                  <StatTile key={o.ownershipType} label={o.ownershipType.replace(/_/g, " ")} value={o.count} />
                ))}
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>By Office</h3>
              <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Office", "Total", "Assigned", "Available", "In Repair", "Booked Value"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {execData.byOffice.map(o => (
                      <tr key={o.officeId ?? "unassigned"} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{o.officeName}</td>
                        <td style={{ padding: "8px 10px" }}>{o.total}</td>
                        <td style={{ padding: "8px 10px" }}>{o.assigned}</td>
                        <td style={{ padding: "8px 10px" }}>{o.available}</td>
                        <td style={{ padding: "8px 10px" }}>{o.inRepair}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {Object.entries(o.bookedByCurrency).length === 0 ? "—" : Object.entries(o.bookedByCurrency).map(([c, v]) => money(c, v)).join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, margin: "20px 0 10px" }}>By Category</h3>
              <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Category", "Count", "% of Estate", "Booked Value"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {execData.byCategory.map(c => (
                      <tr key={c.category} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{c.category.replace(/_/g, " ")}</td>
                        <td style={{ padding: "8px 10px" }}>{c.count}</td>
                        <td style={{ padding: "8px 10px" }}>{pctLabel(c.pctOfEstate)}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {Object.entries(c.bookedByCurrency).length === 0 ? "—" : Object.entries(c.bookedByCurrency).map(([cur, v]) => money(cur, v)).join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, margin: "20px 0 10px" }}>Write-off Trend (6mo)</h3>
              <div style={{ ...cardStyle, overflowX: "auto" }}>
                {execData.writeOffTrend.length === 0
                  ? <p style={{ color: "var(--color-surface-500)", fontSize: 13 }}>No approved disposals in this window</p>
                  : (() => {
                      const max = Math.max(1, ...execData.writeOffTrend.flatMap(t => Object.values(t.byCurrency)));
                      return (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <tbody>
                            {execData.writeOffTrend.map(t => (
                              <tr key={t.month}>
                                <td style={{ padding: "6px 10px", width: 90, fontWeight: 600 }}>{t.month}</td>
                                {Object.entries(t.byCurrency).map(([currency, value]) => (
                                  <td key={currency} style={{ ...barCell(value, max), fontSize: 12 }}>{money(currency, value)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "IT" && isAssetIT && (
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
            {itData?.scope.locked ? (
              <div style={{ ...inputStyle, width: 220, background: "var(--color-surface-50)", color: "var(--color-surface-500)" }}>
                {assetOffices.find(o => o.id === itData.scope.officeId)?.name ?? "Your office"}
              </div>
            ) : (
              <SelectField className="w-auto" value={dashOffice} onChange={e => setDashOffice(e.target.value)}>
                <option value="">All Offices</option>
                {assetOffices.map(o => <option key={o.id} value={o.id}>{o.name} — {o.city}</option>)}
              </SelectField>
            )}
          </div>

          {dashLoading && <p style={{ fontSize: 14, color: "var(--color-surface-500)" }}>Loading…</p>}
          {itData && (
            <>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Ticket SLA &amp; Backlog</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                <StatTile label="Open Tickets" value={itData.tickets.openTotal} />
                <StatTile label="SLA Breached" value={itData.tickets.breachedTotal} tone={itData.tickets.breachedTotal > 0 ? "error" : "default"} />
                <StatTile label="Due Within 24h" value={itData.tickets.dueSoon} tone={itData.tickets.dueSoon > 0 ? "warn" : "default"} />
                <StatTile label="Unassigned" value={itData.tickets.unassigned} />
                <StatTile label="Resolved (7d)" value={itData.tickets.resolved7d} tone="success" />
              </div>
              <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Priority", "Open", "Breached", "Breach %"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {itData.tickets.backlogByPriority.map(r => (
                      <tr key={r.priority} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{r.priority}</td>
                        <td style={{ padding: "8px 10px" }}>{r.open}</td>
                        <td style={{ padding: "8px 10px" }}>{r.breached}</td>
                        <td style={{ padding: "8px 10px" }}>{pctLabel(r.breachRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Verification Exceptions</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                <StatTile label="Active Campaigns" value={itData.verification.activeCampaigns} />
                <StatTile label="Awaiting Response" value={itData.verification.awaitingResponse} />
                <StatTile label="Open Exceptions" value={itData.verification.openExceptions} tone={itData.verification.openExceptions > 0 ? "warn" : "default"} />
              </div>
              {itData.verification.campaigns.length > 0 && (
                <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                      {["Campaign", "Office", "Total", "Verified", "Pending", "Exceptions", "Completion"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {itData.verification.campaigns.map(c => (
                        <tr key={c.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                          <td style={{ padding: "8px 10px", fontWeight: 600 }}>{c.title}</td>
                          <td style={{ padding: "8px 10px" }}>{c.office ? `${c.office.name} — ${c.office.city}` : "All Offices"}</td>
                          <td style={{ padding: "8px 10px" }}>{c.total}</td>
                          <td style={{ padding: "8px 10px" }}>{c.verified}</td>
                          <td style={{ padding: "8px 10px" }}>{c.pending}</td>
                          <td style={{ padding: "8px 10px" }}>{c.exceptions}</td>
                          <td style={{ ...barCell(c.completionPct * 100, 100), fontSize: 12 }}>{pctLabel(c.completionPct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Import Batch Health</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                <StatTile label="Batches Staged" value={itData.imports.stagedBatches} />
                <StatTile label="Error Rows Awaiting Fix" value={itData.imports.errorRowsAwaitingFix} tone={itData.imports.errorRowsAwaitingFix > 0 ? "warn" : "default"} />
                <StatTile label="Unmatched-Employee Rows" value={itData.imports.unmatchedRowsAwaitingFix} />
                <StatTile label="Imported (30d)" value={itData.imports.importedLast30d} tone="success" />
              </div>
              {itData.imports.recentBatches.length > 0 && (
                <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                      {["File", "Office", "Status", "Total", "Valid", "Error", "Imported", "Unmatched", "Uploaded"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {itData.imports.recentBatches.map(b => (
                        <tr key={b.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                          <td style={{ padding: "8px 10px", fontWeight: 600 }}>{b.fileName}</td>
                          <td style={{ padding: "8px 10px" }}>{b.office ? `${b.office.name} — ${b.office.city}` : "—"}</td>
                          <td style={{ padding: "8px 10px" }}>{b.status}</td>
                          <td style={{ padding: "8px 10px" }}>{b.totalRows}</td>
                          <td style={{ padding: "8px 10px" }}>{b.validRows}</td>
                          <td style={{ padding: "8px 10px" }}>{b.errorRows}</td>
                          <td style={{ padding: "8px 10px" }}>{b.importedRows}</td>
                          <td style={{ padding: "8px 10px" }}>{b.unmatchedRows}</td>
                          <td style={{ padding: "8px 10px" }}>{new Date(b.uploadedAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Expiry Runway</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                <StatTile label="Warranty Expired" value={itData.expiry.warrantyExpired} tone={itData.expiry.warrantyExpired > 0 ? "error" : "default"} />
                <StatTile label="Expiring ≤30d" value={itData.expiry.warrantyExpiring30d} tone={itData.expiry.warrantyExpiring30d > 0 ? "warn" : "default"} />
                <StatTile label="Expiring ≤90d" value={itData.expiry.warrantyExpiring90d} />
                <StatTile label="Rentals Ending ≤60d" value={itData.expiry.rentalsEnding60d} />
                <StatTile label="License Seats" value={itData.expiry.licenseSeats} sub={`${itData.expiry.licenseCount} licenses`} />
                <StatTile label="Licenses Missing Renewal Owner" value={itData.expiry.licensesNoRenewalOwner} tone={itData.expiry.licensesNoRenewalOwner > 0 ? "warn" : "default"} />
              </div>
              {itData.expiry.soonestExpiries.length > 0 && (
                <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                      {["Asset Tag", "Name", "Category", "Office", "Expiry", "Status"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {itData.expiry.soonestExpiries.map(a => (
                        <tr key={a.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                          <td style={{ padding: "8px 10px", fontWeight: 600 }}>{a.assetTag}</td>
                          <td style={{ padding: "8px 10px" }}>{a.name}</td>
                          <td style={{ padding: "8px 10px" }}>{a.category.replace(/_/g, " ")}</td>
                          <td style={{ padding: "8px 10px" }}>{a.office ? `${a.office.name} — ${a.office.city}` : "—"}</td>
                          <td style={{ padding: "8px 10px" }}>{new Date(a.warrantyExpiry).toLocaleDateString()}</td>
                          <td style={{ padding: "8px 10px" }}>{a.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Estate Composition</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {itData.estate.map(s => <StatTile key={s.status} label={s.status.replace(/_/g, " ")} value={s.count} />)}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "Finance" && isAssetFinance && (
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
            {finData?.scope.locked ? (
              <div style={{ ...inputStyle, width: 220, background: "var(--color-surface-50)", color: "var(--color-surface-500)" }}>
                {assetOffices.find(o => o.id === finData.scope.officeId)?.name ?? "Your office"}
              </div>
            ) : (
              <SelectField className="w-auto" value={dashOffice} onChange={e => setDashOffice(e.target.value)}>
                <option value="">All Offices</option>
                {assetOffices.map(o => <option key={o.id} value={o.id}>{o.name} — {o.city}</option>)}
              </SelectField>
            )}
          </div>

          {dashLoading && <p style={{ fontSize: 14, color: "var(--color-surface-500)" }}>Loading…</p>}

          {finProc && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                {Object.entries(finProc.statusCounts).map(([status, count]) => (
                  <StatTile key={status} label={status.replace(/_/g, " ")} value={count} />
                ))}
              </div>

              {finProc.spendByCurrency.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                  {finProc.spendByCurrency.map(s => (
                    <div key={s.currency} style={{ ...cardStyle, minWidth: 180, marginBottom: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{s.currency} {s.approvedTotal.toLocaleString("en-IN")}</div>
                      <div style={{ fontSize: 11, color: "var(--color-surface-500)" }}>Approved (awaiting receipt)</div>
                      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 6 }}>{s.currency} {s.receivedTotal.toLocaleString("en-IN")}</div>
                      <div style={{ fontSize: 11, color: "var(--color-surface-500)" }}>Received</div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                <StatTile label="Invoices Flagged" value={finProc.aiVerification.flagged} tone="warn" />
                <StatTile label="Invoices Clean" value={finProc.aiVerification.clean} />
                <StatTile label="Awaiting AI Check" value={finProc.aiVerification.pending} />
              </div>

              {finStock.length > 0 && (
                <>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--color-surface-500)", marginBottom: 8 }}>Current Inventory (Available)</h4>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                    {Object.entries(finStock.reduce<Record<string, number>>((acc, r) => { acc[r.category] = (acc[r.category] ?? 0) + r.count; return acc; }, {}))
                      .map(([category, count]) => <StatTile key={category} label={`${category.replace(/_/g, " ")} available`} value={count} />)}
                  </div>
                </>
              )}

              <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--color-surface-500)", marginBottom: 8 }}>By Office</h4>
              <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Office", "Total Requests", "Pending Approval", "Spend"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {finProc.byOffice.map(o => (
                      <tr key={o.officeId} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{o.officeName} — {o.city}</td>
                        <td style={{ padding: "8px 10px" }}>{o.totalRequests}</td>
                        <td style={{ padding: "8px 10px" }}>{o.pendingApproval}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {o.spend.length === 0 ? "—" : o.spend.map(s => `${s.currency} ${(s.approvedTotal + s.receivedTotal).toLocaleString("en-IN")}`).join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--color-surface-500)", margin: 0 }}>Recent Activity</h4>
                <SelectField size="sm" className="w-auto" value={recentStatus} onChange={e => setRecentStatus(e.target.value)}>
                  <option value="">All Statuses</option>
                  {["SUBMITTED", "FINANCE_APPROVED", "FINANCE_REJECTED", "RECEIVED", "CANCELLED"].map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                  ))}
                </SelectField>
              </div>
              <div style={{ ...cardStyle, marginBottom: 24, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Item", "Office", "Status", "Requested By", "Finance Action", "Received By", "AI Verdict"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {finProc.recent.filter(r => !recentStatus || r.status === recentStatus).map(r => (
                      <tr key={r.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                        <td style={{ padding: "8px 10px" }}>{r.quantity}× {r.model || r.category}</td>
                        <td style={{ padding: "8px 10px" }}>{r.office.name}</td>
                        <td style={{ padding: "8px 10px" }}>{r.status.replace(/_/g, " ")}</td>
                        <td style={{ padding: "8px 10px" }}>{r.requestedBy.firstName} {r.requestedBy.lastName}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {r.financeActionBy ? `${r.financeActionBy.firstName} ${r.financeActionBy.lastName}` : "—"}
                          {r.financeRemarks && <div style={{ fontSize: 11, color: "var(--color-surface-500)" }}>{r.financeRemarks}</div>}
                        </td>
                        <td style={{ padding: "8px 10px" }}>{r.receivedBy ? `${r.receivedBy.firstName} ${r.receivedBy.lastName}` : "—"}</td>
                        <td style={{ padding: "8px 10px" }}>{r.aiVerificationResult ? r.aiVerificationResult.verdict : r.invoiceS3Key ? "Pending" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {finData && (
            <>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Disposal Approval Queue</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                {Object.entries(finData.disposals.statusCounts).map(([status, count]) => (
                  <StatTile key={status} label={status.replace(/_/g, " ")} value={count} />
                ))}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                {finData.disposals.pendingByCurrency.map(p => (
                  <StatTile key={p.currency} label={`Pending Finance (${p.currency})`} value={`${p.count} · ${money(p.currency, p.value)}`} tone={p.count > 0 ? "warn" : "default"} />
                ))}
                {finData.disposals.approved90dByCurrency.map(a => (
                  <StatTile key={a.currency} label={`Approved Write-offs 90d (${a.currency})`} value={money(a.currency, a.value)} tone="success" />
                ))}
                <StatTile label="Auto-Approved (90d)" value={finData.disposals.autoApproved90d} />
                <StatTile label="Finance-Approved (90d)" value={finData.disposals.financeApproved90d} />
              </div>
              {finData.disposals.pendingQueue.length > 0 && (
                <div style={{ ...cardStyle, marginBottom: 20, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: "1px solid var(--color-surface-200)" }}>
                      {["Asset", "Office", "Value", "Days Waiting", "Wipe"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", color: "var(--color-surface-500)" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {finData.disposals.pendingQueue.map(r => (
                        <tr key={r.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                          <td style={{ padding: "8px 10px", fontWeight: 600 }}>{r.asset.assetTag} — {r.asset.name}</td>
                          <td style={{ padding: "8px 10px" }}>{r.asset.office ? `${r.asset.office.name} — ${r.asset.office.city}` : "—"}</td>
                          <td style={{ padding: "8px 10px" }}>{money(r.currency, Number(r.estimatedValue))}</td>
                          <td style={{ padding: "8px 10px" }}>{r.daysWaiting}</td>
                          <td style={{ padding: "8px 10px" }}>{r.wipeRequired ? (r.wipeConfirmed ? "Confirmed" : "Pending") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Custody Pipeline</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                <StatTile label="Awaiting Custody Confirmation" value={finData.custody.awaitingConfirmation} tone={finData.custody.awaitingConfirmation > 0 ? "warn" : "default"} />
                <StatTile label="Held in Finance Custody" value={finData.custody.heldInCustody} />
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Recurring Cost Exposure</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                {finData.recurringCost.rentalByCurrency.map(r => (
                  <StatTile key={`rental-${r.currency}`} label={`Rental / mo (${r.currency})`} value={money(r.currency, r.monthly)} sub={`${r.count} assets`} />
                ))}
                {finData.recurringCost.telecomByCurrency.map(t => (
                  <StatTile key={`telecom-${t.currency}`} label={`Telecom / mo (${t.currency})`} value={money(t.currency, t.monthly)} sub={`${t.count} lines`} />
                ))}
              </div>

              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Booked Asset Cost</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {finData.bookedCost.byCurrency.map(b => (
                  <StatTile key={b.currency} label={`Booked Value (${b.currency})`} value={money(b.currency, b.value)} sub={`${b.count} assets`} />
                ))}
                {finData.bookedCost.missingPrice > 0 && (
                  <StatTile label="Missing Purchase Price" value={finData.bookedCost.missingPrice} tone="warn" />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
